import dayjs from 'dayjs'

/**
 * Dashboard（/index）の業務日レンジ共通ロジック。
 *
 * 単日モード（start === end）とレンジモードを 1 セットの状態で扱うため、
 * 「上限3か月の判定」「エラーメッセージ」「API パラメータ組み立て」「表示ラベル」を
 * ここに集約する。バックエンド（DpDateRange）と同じ判定にそろえること：
 * - 終了日 < 開始日 → NG
 * - 終了日 > 開始日 + 3か月（暦月ベース）→ NG
 */

/** 単次で選択できる最長期間（暦月ベース） */
export const MAX_RANGE_MONTHS = 3

const WEEK_JP = ['日', '月', '火', '水', '木', '金', '土']

/** 選択できる終了日の上限（開始日 + 3か月。例：1/15 → 4/15 まで可） */
export function maxEndDate(start) {
  return dayjs(start).startOf('day').add(MAX_RANGE_MONTHS, 'month')
}

/** レンジ（開始日と終了日が別日）かどうか */
export function isRangeMode(start, end) {
  if (!start || !end) return false
  return !dayjs(start).startOf('day').isSame(dayjs(end).startOf('day'), 'day')
}

/**
 * レンジの妥当性チェック。バックエンドの INVALID_DATE_RANGE と同じ条件で、
 * 送信前にフロント側で止めてメッセージを出すためのもの。
 *
 * @returns {{valid: boolean, message: string}}
 */
export function validateRange(start, end) {
  const s = dayjs(start).startOf('day')
  const e = dayjs(end).startOf('day')
  if (!s.isValid() || !e.isValid()) {
    return { valid: false, message: '日付の形式が正しくありません' }
  }
  if (e.isBefore(s)) {
    return { valid: false, message: '終了日は開始日以降の日付を選択してください' }
  }
  if (e.isAfter(maxEndDate(s))) {
    return { valid: false, message: `選択できる期間は最長${MAX_RANGE_MONTHS}か月です` }
  }
  return { valid: true, message: '' }
}

/**
 * API に渡す日付パラメータ。単日なら従来どおり date、レンジなら startDate/endDate。
 * 単日で date を使い続けるのは、当日・全国・デフォルト件数のリクエストが
 * サーバー側の Redis 予熱キャッシュに乗るのを壊さないため。
 */
export function buildDateParams(start, end) {
  const s = dayjs(start).format('YYYY-MM-DD')
  const e = dayjs(end).format('YYYY-MM-DD')
  return s === e ? { date: e } : { startDate: s, endDate: e }
}

/** 「2026年8月24日（日）」 */
export function formatDateLabel(date) {
  const d = dayjs(date)
  return `${d.year()}年${d.month() + 1}月${d.date()}日（${WEEK_JP[d.day()]}）`
}

/** ヘッダー等に出す期間ラベル。単日なら 1 日分、レンジなら「〜」でつなぐ */
export function formatRangeLabel(start, end) {
  const s = dayjs(start)
  const e = dayjs(end)
  if (!isRangeMode(s, e)) return `${s.year()}年${s.month() + 1}月${s.date()}日`
  return `${s.year()}年${s.month() + 1}月${s.date()}日 〜 ${e.year()}年${e.month() + 1}月${e.date()}日`
}

/** 日数（両端含む）。単日は 1 */
export function rangeDayCount(start, end) {
  return dayjs(end).startOf('day').diff(dayjs(start).startOf('day'), 'day') + 1
}
